import {
  useQuery,
  UseQueryResult,
  keepPreviousData,
} from '@tanstack/react-query'
import {
  SiteRFPDocumentSummariesResponseBody,
  SiteRFPDocumentSummary,
  SiteRFPDocumentSummarySortByParam,
  SiteRFPPhaseAPIResponse,
} from '@black-bear-energy/black-bear-energy-common'
import { AxiosInstance } from 'axios'
import { useApiWithIDTokenGranted } from '../../state/modules/common'
import { transformSiteRFPDocumentSummary } from './helpers/transformSiteRFPDocumentSummary'

/**
 * Fetch paginated site RFP document summaries for a customer RFP for populating the Document Exchange customer RFP dashboard.
 * @param customerRFPId - The ID of the customer RFP.
 * @param search - The search term to filter by.
 * This can be a site name or an address.
 * @param requiredMissing - Whether to filter for sites with required documents missing.
 * @param reviewMissing - Whether to filter for sites with documents that need review.
 * @param phase - The site RFP phases to filter by.
 * @param sortBy - The field to sort by.
 * @param sortAsc - If false, the results will be sorted in descending order.
 * @param page - The page number to fetch, starting at 1.
 * @returns A UseQueryResult object containing the site RFP document summaries and pagination information.
 */
export const useSiteRFPDocumentSummaries = (
  customerRFPId: number,
  search: string = '',
  requiredMissing: boolean = false,
  reviewMissing: boolean = false,
  phase: SiteRFPPhaseAPIResponse[] = [],
  sortBy: SiteRFPDocumentSummarySortByParam = SiteRFPDocumentSummarySortByParam.SiteName,
  sortAsc: boolean = true,
  page: number = 1
): UseQueryResult<SiteRFPDocumentSummariesResult, Error> => {
  const api = useApiWithIDTokenGranted()

  return useQuery({
    queryKey: [
      api,
      'siteRFPDocumentSummaries',
      customerRFPId,
      search,
      requiredMissing,
      reviewMissing,
      phase,
      sortBy,
      sortAsc,
      page,
    ],
    queryFn: () =>
      fetchSiteRFPDocumentSummaries(
        api as AxiosInstance,
        customerRFPId,
        search,
        requiredMissing,
        reviewMissing,
        phase,
        sortBy,
        sortAsc,
        page
      ),
    select: transformSiteRFPDocumentSummaries,
    placeholderData: keepPreviousData,
    enabled: !!api,
  })
}

/** The result from the useSiteRFPDocumentSummaries hook. */
export type SiteRFPDocumentSummariesResult = {
  records: SiteRFPDocumentSummary[]
  pagination: SiteRFPDocumentSummariesResponseBody['pagination']
}

async function fetchSiteRFPDocumentSummaries(
  api: AxiosInstance,
  customerRFPId: number,
  search: string,
  requiredMissing: boolean,
  reviewMissing: boolean,
  phase: SiteRFPPhaseAPIResponse[],
  sortBy: SiteRFPDocumentSummarySortByParam,
  sortAsc: boolean,
  page: number
): Promise<SiteRFPDocumentSummariesResponseBody> {
  const response = await api.get<SiteRFPDocumentSummariesResponseBody>(
    `/customer-rfps/${customerRFPId}/site-rfp-document-summaries`,
    {
      params: {
        search: encodeURIComponent(search),
        requiredMissing,
        reviewMissing,
        phase,
        sortBy,
        sortAsc,
        page,
      },
    }
  )
  return response.data
}

const transformSiteRFPDocumentSummaries = (
  data: SiteRFPDocumentSummariesResponseBody
): SiteRFPDocumentSummariesResult => {
  const records = data.records.map((responseElement) =>
    transformSiteRFPDocumentSummary(responseElement)
  )
  return {
    records,
    pagination: data.pagination,
  }
}
